import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Download, BarChart3, PieChart, Grid3x3, TrendingUp, TrendingDown, Calendar as CalendarIcon, CreditCard, X } from 'lucide-react';
import { BarChart, Bar, PieChart as RePieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api, { appointmentsAPI, branchesAPI } from '../services/api';
// import { toast } from '../hooks/use-toast'; // Disabled to avoid portal errors

// Simple toast replacement
const toast = ({ title, description, variant }) => {
  console.log(`[${variant === 'destructive' ? 'ERROR' : 'INFO'}] ${title}: ${description}`);
};

const Statistics = () => {
  const { currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // States
  const [appointments, setAppointments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [userCanExport, setUserCanExport] = useState(false);
  
  // Filters
  const [timeRange, setTimeRange] = useState('month'); // week, month, year
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [selectedService, setSelectedService] = useState('all');
  
  // View mode
  const [chartType, setChartType] = useState('bar'); // bar, pie, numbers
  
  // Chart colors
  const COLORS = {
    completed: '#10B981', // green
    cancelled: '#EF4444', // red
  };

  useEffect(() => {
    // Don't redirect while still loading auth state
    if (authLoading) {
      return;
    }
    
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    loadData();
    checkSubscriptionStatus();
  }, [currentUser, authLoading, navigate]);

  // Check for payment callback
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const paymentStatus = searchParams.get('payment');
    
    if (sessionId && paymentStatus === 'success') {
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === 'cancelled') {
      toast({
        title: 'Pago cancelado',
        description: 'El proceso de pago fue cancelado',
        variant: 'destructive',
      });
    }
  }, [searchParams]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [appointmentsRes, branchesRes] = await Promise.all([
        appointmentsAPI.getAll(),
        branchesAPI.getAll(),
      ]);
      
      setAppointments(appointmentsRes.data);
      setBranches(branchesRes.data);
      
      // Extract unique services from appointments
      const uniqueServices = [...new Set(appointmentsRes.data
        .map(apt => apt.serviceType)
        .filter(Boolean))];
      setServices(uniqueServices);
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los datos',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter appointments based on selected filters
  const getFilteredAppointments = () => {
    let filtered = [...appointments];
    
    // Time range filter
    const now = new Date();
    const startDate = new Date();
    
    if (timeRange === 'week') {
      startDate.setDate(now.getDate() - 7);
    } else if (timeRange === 'month') {
      startDate.setMonth(now.getMonth() - 1);
    } else if (timeRange === 'year') {
      startDate.setFullYear(now.getFullYear() - 1);
    }
    
    filtered = filtered.filter(apt => {
      const aptDate = new Date(apt.date);
      return aptDate >= startDate && aptDate <= now;
    });
    
    // Branch filter
    if (selectedBranch !== 'all') {
      filtered = filtered.filter(apt => apt.branchId === selectedBranch);
    }
    
    // Service filter
    if (selectedService !== 'all') {
      filtered = filtered.filter(apt => apt.serviceType === selectedService);
    }
    
    return filtered;
  };

  // Calculate statistics
  const getStatistics = () => {
    const filtered = getFilteredAppointments();
    
    const total = filtered.length;
    const completed = filtered.filter(apt => 
      apt.status === 'confirmed' || apt.status === 'completed'
    ).length;
    const cancelled = filtered.filter(apt => 
      apt.status === 'cancelled' || apt.status === 'no_show'
    ).length;
    
    const completedPercentage = total > 0 ? ((completed / total) * 100).toFixed(1) : 0;
    const cancelledPercentage = total > 0 ? ((cancelled / total) * 100).toFixed(1) : 0;
    
    return {
      total,
      completed,
      cancelled,
      completedPercentage,
      cancelledPercentage,
    };
  };

  const stats = getStatistics();

  // Chart data
  const chartData = [
    { name: 'Completadas', value: stats.completed, color: COLORS.completed },
    { name: 'Canceladas', value: stats.cancelled, color: COLORS.cancelled },
  ];

  // Check subscription status
  const checkSubscriptionStatus = async () => {
    try {
      const response = await api.get('/api/payments/subscription-status');
      setSubscriptionStatus(response.data);
      
      // Get user export permission
      const userResponse = await api.get('/api/auth/me');
      setUserCanExport(userResponse.data.can_export !== false);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  // Check if user can export
  const canExport = () => {
    if (currentUser?.role === 'admin') return true;
    if (!subscriptionStatus) return false;
    return subscriptionStatus.is_premium;
  };

  // Handle export with premium check
  const handleExport = async (format) => {
    // Check if user can export
    if (!canExport()) {
      setShowUpgradeModal(true);
      return;
    }

    try {
      const params = new URLSearchParams();
      if (selectedBranch !== 'all') params.append('branch_id', selectedBranch);
      if (selectedService !== 'all') params.append('service_type', selectedService);
      params.append('format', format);
      
      // Get the filtered date range
      const now = new Date();
      const startDate = new Date();
      
      if (timeRange === 'week') {
        startDate.setDate(now.getDate() - 7);
      } else if (timeRange === 'month') {
        startDate.setMonth(now.getMonth() - 1);
      } else if (timeRange === 'year') {
        startDate.setFullYear(now.getFullYear() - 1);
      }
      
      params.append('start_date', startDate.toISOString().split('T')[0]);
      params.append('end_date', now.toISOString().split('T')[0]);

      const response = await api.get(`/api/stats/export-summary?${params.toString()}`, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', format === 'pdf' ? 'estadisticas.pdf' : 'estadisticas.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast({
        title: 'Exportación exitosa',
        description: `El archivo ${format.toUpperCase()} se ha descargado correctamente`,
      });
    } catch (error) {
      console.error('Error exporting:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo exportar el archivo',
        variant: 'destructive',
      });
    }
  };

  // Handle export contacts
  const handleExportContacts = async (format) => {
    // Check if user can export
    if (!canExport()) {
      setShowUpgradeModal(true);
      return;
    }

    try {
      const params = new URLSearchParams();
      if (selectedBranch !== 'all') params.append('branch_id', selectedBranch);
      params.append('format', format);

      const response = await api.get(`/api/stats/export-contacts?${params.toString()}`, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', format === 'pdf' ? 'contactos.pdf' : 'contactos.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast({
        title: 'Exportación exitosa',
        description: `Los contactos se han descargado en formato ${format.toUpperCase()}`,
      });
    } catch (error) {
      console.error('Error exporting contacts:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudieron exportar los contactos',
        variant: 'destructive',
      });
    }
  };

  // Create checkout session for premium upgrade
  const handleUpgradeToPremium = async (packageId) => {
    setProcessingPayment(true);
    try {
      const response = await api.post('/api/payments/create-checkout-session', null, {
        params: { package_id: packageId }
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      toast({
        title: 'Error',
        description: 'No se pudo iniciar el proceso de pago',
        variant: 'destructive',
      });
      setProcessingPayment(false);
    }
  };

  // Poll payment status after returning from Stripe
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      toast({
        title: 'Verificación pendiente',
        description: 'El pago está siendo procesado. Por favor recarga la página en unos momentos.',
      });
      return;
    }

    try {
      const response = await api.get(`/api/payments/checkout-status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        toast({
          title: '¡Pago exitoso!',
          description: 'Tu suscripción premium ha sido activada.',
        });
        // Refresh subscription status
        await checkSubscriptionStatus();
        setShowUpgradeModal(false);
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      } else if (response.data.status === 'expired') {
        toast({
          title: 'Sesión expirada',
          description: 'La sesión de pago ha expirado. Por favor intenta nuevamente.',
          variant: 'destructive',
        });
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast({
        title: 'Error',
        description: 'Error al verificar el estado del pago',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Cargando estadísticas...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                size="sm"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">📊 Estadísticas</h1>
            </div>
            
            {/* Export buttons - For premium, admin, or users with can_export=true */}
            {(subscriptionStatus?.is_premium || currentUser?.role === 'admin' || userCanExport) ? (
              <div className="flex items-center space-x-2 flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleExport('pdf')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleExport('excel')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Excel
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleExportContacts('excel')}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Contactos
                </Button>
              </div>
            ) : (
              // Show upgrade button for users without export permission
              <Button
                onClick={() => setShowUpgradeModal(true)}
                size="sm"
                className="bg-gradient-to-r from-blue-500 to-purple-500 text-white"
              >
                ✨ Actualizar a Premium
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Hero Section with Gradient - Fills empty space on mobile */}
        <div className="mb-6 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-lg p-6 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2">📊 Panel de Estadísticas</h2>
              <p className="text-blue-100">Análisis completo de tus citas y rendimiento</p>
            </div>
            <div className="hidden md:block">
              <div className="text-right">
                <p className="text-3xl font-bold">{stats.total}</p>
                <p className="text-sm text-blue-100">Citas Totales</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Filtros</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Período</Label>
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="week">Última semana</option>
                  <option value="month">Último mes</option>
                  <option value="year">Último año</option>
                </select>
              </div>
              
              <div className="space-y-2">
                <Label>Sucursal</Label>
                <select
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="all">Todas las sucursales</option>
                  {branches.map(branch => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="space-y-2">
                <Label>Servicio</Label>
                <select
                  value={selectedService}
                  onChange={(e) => setSelectedService(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="all">Todos los servicios</option>
                  {services.map(service => (
                    <option key={service} value={service}>
                      {service}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total de Citas</p>
                  <h3 className="text-3xl font-bold text-gray-900">{stats.total}</h3>
                </div>
                <CalendarIcon className="w-12 h-12 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Completadas</p>
                  <h3 className="text-3xl font-bold text-green-600">{stats.completed}</h3>
                  <p className="text-sm text-gray-500 flex items-center mt-1">
                    <TrendingUp className="w-4 h-4 mr-1" />
                    {stats.completedPercentage}%
                  </p>
                </div>
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">✅</span>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Canceladas</p>
                  <h3 className="text-3xl font-bold text-red-600">{stats.cancelled}</h3>
                  <p className="text-sm text-gray-500 flex items-center mt-1">
                    <TrendingDown className="w-4 h-4 mr-1" />
                    {stats.cancelledPercentage}%
                  </p>
                </div>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="text-2xl">❌</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Chart Type Selector */}
        <div className="flex justify-center mb-6 space-x-2">
          <Button
            variant={chartType === 'bar' ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setChartType('bar')}
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Barras
          </Button>
          <Button
            variant={chartType === 'pie' ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setChartType('pie')}
          >
            <PieChart className="w-4 h-4 mr-2" />
            Torta
          </Button>
          <Button
            variant={chartType === 'numbers' ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setChartType('numbers')}
          >
            <Grid3x3 className="w-4 h-4 mr-2" />
            Números
          </Button>
        </div>

        {/* Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Distribución de Citas</CardTitle>
          </CardHeader>
          <CardContent>
            {chartType === 'bar' && (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#8884d8">
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
            
            {chartType === 'pie' && (
              <ResponsiveContainer width="100%" height={400}>
                <RePieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={150}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </RePieChart>
              </ResponsiveContainer>
            )}
            
            {chartType === 'numbers' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-8">
                <div className="text-center">
                  <div className="w-32 h-32 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-5xl">✅</span>
                  </div>
                  <h3 className="text-4xl font-bold text-green-600 mb-2">{stats.completed}</h3>
                  <p className="text-lg text-gray-600">Completadas</p>
                  <Badge className="mt-2 bg-green-500">{stats.completedPercentage}%</Badge>
                </div>
                
                <div className="text-center">
                  <div className="w-32 h-32 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-5xl">❌</span>
                  </div>
                  <h3 className="text-4xl font-bold text-red-600 mb-2">{stats.cancelled}</h3>
                  <p className="text-lg text-gray-600">Canceladas</p>
                  <Badge className="mt-2 bg-red-500">{stats.cancelledPercentage}%</Badge>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upgrade to Premium Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900">🎉 Actualiza a Premium</h2>
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <p className="text-gray-600 mb-6">
                Las funciones de exportación están disponibles solo para usuarios premium. 
                Elige un plan para desbloquear todas las funcionalidades:
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Monthly Plan */}
                <div className="border-2 border-blue-200 rounded-lg p-6 hover:border-blue-500 transition-colors">
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Premium Mensual</h3>
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-blue-600">$14</span>
                      <span className="text-gray-500 ml-2">/mes</span>
                    </div>
                  </div>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Exportación a PDF y Excel</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Exportación de contactos</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Estadísticas avanzadas</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Soporte prioritario</span>
                    </li>
                  </ul>
                  <Button
                    onClick={() => handleUpgradeToPremium('premium_monthly')}
                    disabled={processingPayment}
                    className="w-full"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    {processingPayment ? 'Procesando...' : 'Suscribirse'}
                  </Button>
                </div>

                {/* Annual Plan */}
                <div className="border-2 border-green-200 rounded-lg p-6 hover:border-green-500 transition-colors relative">
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-green-500 text-white px-3 py-1">¡Ahorra 16%!</Badge>
                  </div>
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Premium Anual</h3>
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-green-600">$140</span>
                      <span className="text-gray-500 ml-2">/año</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">Solo $11.67/mes</p>
                  </div>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Exportación a PDF y Excel</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Exportación de contactos</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Estadísticas avanzadas</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600">Soporte prioritario</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="text-sm text-gray-600 font-semibold">2 meses gratis</span>
                    </li>
                  </ul>
                  <Button
                    onClick={() => handleUpgradeToPremium('premium_annual')}
                    disabled={processingPayment}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    {processingPayment ? 'Procesando...' : 'Suscribirse'}
                  </Button>
                </div>
              </div>

              <div className="text-center text-sm text-gray-500">
                <p>💳 Pago seguro procesado por Stripe</p>
                <p className="mt-1">Cancela en cualquier momento sin compromiso</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Statistics;
